import React, { useEffect, useState } from "react";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit2, Trash2, Mail, Play, Square, CheckCircle2, AlertCircle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function MailboxModal({ mailbox, templates, onSave, onClose }) {
  const [form, setForm] = useState({
    name: mailbox?.name || "", imap_host: mailbox?.imap_host || "",
    imap_port: mailbox?.imap_port || 993, imap_user: mailbox?.imap_user || "",
    imap_password: "", imap_use_ssl: mailbox?.imap_use_ssl ?? true,
    imap_folder: mailbox?.imap_folder || "INBOX",
    polling_interval_minutes: mailbox?.polling_interval_minutes || 5,
    template_id: mailbox?.template_id || "", reply_to_email: mailbox?.reply_to_email || "",
    is_active: mailbox?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    if (!form.name || !form.imap_host || !form.imap_user) { setError("Name, host, and username required"); return; }
    setSaving(true);
    try { await onSave(form); onClose(); }
    catch (e) { setError(formatApiError(e)); setSaving(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{mailbox ? "Edit" : "Add"} IMAP Mailbox</DialogTitle></DialogHeader>
        {error && <div className="p-2 bg-red-50 rounded text-red-700 text-sm">{error}</div>}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-gray-500">Mailbox Name *</Label><Input className="mt-1" value={form.name} onChange={e => set("name", e.target.value)} placeholder="Sales Reports" /></div>
            <div><Label className="text-xs text-gray-500">Reply-To Email</Label><Input className="mt-1" type="email" value={form.reply_to_email} onChange={e => set("reply_to_email", e.target.value)} placeholder="results@company.com" /></div>
          </div>
          <div className="p-3 bg-[#F8F9FA] border border-gray-200 rounded-md space-y-3">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">IMAP Connection</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-gray-500">IMAP Host *</Label><Input className="mt-1" value={form.imap_host} onChange={e => set("imap_host", e.target.value)} placeholder="imap.gmail.com" /></div>
              <div><Label className="text-xs text-gray-500">Port</Label><Input className="mt-1" type="number" value={form.imap_port} onChange={e => set("imap_port", parseInt(e.target.value))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-gray-500">Username *</Label><Input className="mt-1" value={form.imap_user} onChange={e => set("imap_user", e.target.value)} placeholder="you@gmail.com" /></div>
              <div><Label className="text-xs text-gray-500">Password{mailbox ? " (leave blank to keep)" : " *"}</Label><Input className="mt-1" type="password" value={form.imap_password} onChange={e => set("imap_password", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs text-gray-500">Folder</Label><Input className="mt-1" value={form.imap_folder} onChange={e => set("imap_folder", e.target.value)} /></div>
              <div className="flex items-center gap-2 mt-5"><Switch checked={form.imap_use_ssl} onCheckedChange={v => set("imap_use_ssl", v)} /><Label className="text-xs text-gray-600">Use SSL</Label></div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Poll Every (minutes)</Label>
              <Input className="mt-1" type="number" min={1} value={form.polling_interval_minutes} onChange={e => set("polling_interval_minutes", parseInt(e.target.value))} />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Auto-Apply Template</Label>
              <Select value={form.template_id || "__none__"} onValueChange={v => set("template_id", v === "__none__" ? null : v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="None (no auto-run)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None (no auto-run)</SelectItem>
                  {templates.map(t => <SelectItem key={t._id} value={t._id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={v => set("is_active", v)} /><Label className="text-xs text-gray-600">Active (monitored)</Label></div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Mailbox"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MailboxMonitorPage() {
  const [mailboxes, setMailboxes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMb, setEditingMb] = useState(null);
  const [testing, setTesting] = useState(null);
  const [triggering, setTriggering] = useState(null);
  const [testResult, setTestResult] = useState({});

  useEffect(() => {
    load();
    client.get("/templates/").then(r => setTemplates(r.data)).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    const res = await client.get("/admin/mailboxes").catch(() => ({ data: [] }));
    setMailboxes(res.data);
    setLoading(false);
  };

  const save = async (form) => {
    if (editingMb) await client.put(`/admin/mailboxes/${editingMb._id}`, form);
    else await client.post("/admin/mailboxes", form);
    await load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this mailbox?")) return;
    await client.delete(`/admin/mailboxes/${id}`);
    load();
  };

  const test = async (id) => {
    setTesting(id);
    try {
      const res = await client.post(`/admin/mailboxes/${id}/test`);
      setTestResult(r => ({ ...r, [id]: { ok: true, msg: res.data.message } }));
    } catch (e) {
      setTestResult(r => ({ ...r, [id]: { ok: false, msg: formatApiError(e) } }));
    } finally { setTesting(null); }
  };

  const trigger = async (id) => {
    setTriggering(id);
    try {
      const res = await client.post(`/admin/mailboxes/${id}/trigger`);
      setTestResult(r => ({ ...r, [id]: { ok: true, msg: res.data.message } }));
      setTimeout(() => load(), 3000); // Refresh after 3s
    } catch (e) {
      setTestResult(r => ({ ...r, [id]: { ok: false, msg: formatApiError(e) } }));
    } finally { setTriggering(null); }
  };

  const templateName = (id) => templates.find(t => t._id === id)?.name || "–";

  return (
    <div className="p-6 max-w-5xl mx-auto fade-in">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold text-gray-900">IMAP Mailboxes</h1><p className="text-gray-500 text-sm">Monitor email inboxes and auto-process attachments</p></div>
        <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={() => { setEditingMb(null); setShowModal(true); }}><Plus size={16} className="mr-2" /> Add Mailbox</Button>
      </div>
      <div className="bg-[#e6f2f5] border border-[#006080]/20 rounded-md p-3 mb-5 text-xs text-[#006080]">
        <strong>How it works:</strong> When a new email arrives with an Excel/CSV attachment, the file is automatically imported, the associated template applied, the API called, and the results emailed back to the sender.
      </div>
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 bg-white border border-gray-200 rounded-lg animate-pulse" />)}</div>
      ) : mailboxes.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
          <Mail size={40} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-400">No mailboxes configured</p>
          <Button className="mt-4 bg-[#006080] hover:bg-[#004d66] text-white" onClick={() => setShowModal(true)}>Add your first mailbox</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {mailboxes.map(mb => (
            <div key={mb._id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${mb.is_active ? "bg-[#e6f2f5]" : "bg-gray-100"}`}>
                  <Mail size={18} className={mb.is_active ? "text-[#006080]" : "text-gray-400"} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-900">{mb.name}</p>
                    <Badge variant="secondary" className={`text-xs ${mb.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>{mb.is_active ? "Monitoring" : "Paused"}</Badge>
                  </div>
                  <p className="text-xs text-gray-400 font-mono mt-0.5">{mb.imap_user}@{mb.imap_host}</p>
                  <div className="flex gap-3 mt-1.5 text-xs text-gray-400 flex-wrap">
                    <span>Every {mb.polling_interval_minutes} min</span>
                    <span>Template: {templateName(mb.template_id)}</span>
                    {mb.reply_to_email && <span>Reply to: {mb.reply_to_email}</span>}
                    {mb.last_checked_at && <span>Last checked: {new Date(mb.last_checked_at).toLocaleString()}</span>}
                  </div>
                  {mb.last_error && <p className="text-xs text-red-500 mt-1">Error: {mb.last_error}</p>}
                  {testResult[mb._id] && (
                    <div className={`mt-1.5 text-xs flex items-center gap-1 ${testResult[mb._id].ok ? "text-green-600" : "text-red-600"}`}>
                      {testResult[mb._id].ok ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
                      {testResult[mb._id].msg}
                    </div>
                  )}
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => test(mb._id)} disabled={testing === mb._id}>{testing === mb._id ? "Testing..." : "Test"}</Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-[#006080] text-[#006080] hover:bg-[#e6f2f5]" onClick={() => trigger(mb._id)} disabled={triggering === mb._id} title="Force immediate processing now">{triggering === mb._id ? "Running..." : "Run Now"}</Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => { setEditingMb(mb); setShowModal(true); }}><Edit2 size={12} /></Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-500 hover:bg-red-50" onClick={() => del(mb._id)}><Trash2 size={12} /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {showModal && <MailboxModal mailbox={editingMb} templates={templates} onSave={save} onClose={() => { setShowModal(false); setEditingMb(null); }} />}
    </div>
  );
}
